import React, { useEffect, useState, useCallback } from "react";
import {
  Box, Paper, Typography, Grid, Skeleton, Chip, IconButton, Tooltip, Button,
  useTheme, alpha,
} from "@mui/material";
import {
  EmailOutlined, PeopleOutlined, WarningAmberOutlined, TimerOutlined,
  RequestQuoteOutlined, BookOnlineOutlined, CheckCircleOutlined, CancelOutlined,
  FlagOutlined, StarOutlined, TaskAltOutlined, AssignmentLateOutlined,
  RefreshOutlined, ArrowBackOutlined, TrendingUpOutlined,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import {
  fetchDashboard, DashboardData, DashboardStats, DashboardCharts,
} from "./emailHubV3Api";

/* ── Theme ─────────────────────────────────────────────────────────── */
const T = {
  navy: "#16233A", gold: "#7C4DFF", white: "#fff", slate: "#64748b",
  bg: "#f8fafc", cardBg: "#ffffff",
};

/* ── Stat Card ─────────────────────────────────────────────────────── */
interface StatCardProps {
  title: string; value: number | string; icon: React.ReactNode;
  color: string; subtitle?: string; onClick?: () => void;
}

const StatCard: React.FC<StatCardProps> = ({ title, value, icon, color, subtitle, onClick }) => (
  <Paper
    onClick={onClick}
    sx={{
      p: 2.5, borderRadius: 3, cursor: onClick ? "pointer" : "default",
      background: T.cardBg, border: "1px solid #e2e8f0",
      transition: "all 0.2s", position: "relative", overflow: "hidden",
      "&:hover": onClick ? { boxShadow: "0 8px 25px rgba(0,0,0,0.08)", transform: "translateY(-2px)" } : {},
    }}
  >
    <Box sx={{
      position: "absolute", top: -10, right: -10, width: 80, height: 80,
      borderRadius: "50%", background: alpha(color, 0.08),
    }} />
    <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between" }}>
      <Box>
        <Typography variant="body2" sx={{ color: T.slate, fontWeight: 500, mb: 0.5, fontSize: 12 }}>
          {title}
        </Typography>
        <Typography variant="h4" sx={{ fontWeight: 700, color: T.navy, lineHeight: 1.2 }}>
          {value}
        </Typography>
        {subtitle && (
          <Typography variant="caption" sx={{ color: T.slate, mt: 0.5 }}>
            {subtitle}
          </Typography>
        )}
      </Box>
      <Box sx={{
        width: 44, height: 44, borderRadius: 2, display: "flex", alignItems: "center",
        justifyContent: "center", background: alpha(color, 0.1), color, flexShrink: 0,
      }}>
        {icon}
      </Box>
    </Box>
  </Paper>
);

/* ── Bar Chart (pure CSS) ──────────────────────────────────────────── */
const SimpleBarChart: React.FC<{ data: { label: string; value: number; color: string }[]; title: string }> = ({ data, title }) => {
  const max = Math.max(...data.map(d => d.value), 1);
  return (
    <Paper sx={{ p: 3, borderRadius: 3, border: "1px solid #e2e8f0", height: "100%" }}>
      <Typography variant="subtitle1" sx={{ fontWeight: 600, color: T.navy, mb: 2 }}>{title}</Typography>
      <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
        {data.map((d, i) => (
          <Box key={i}>
            <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.5 }}>
              <Typography variant="caption" sx={{ color: T.slate, fontWeight: 500 }}>{d.label}</Typography>
              <Typography variant="caption" sx={{ fontWeight: 600, color: T.navy }}>{d.value}</Typography>
            </Box>
            <Box sx={{ width: "100%", height: 8, borderRadius: 4, background: "#f1f5f9" }}>
              <Box sx={{
                height: "100%", borderRadius: 4, background: d.color,
                width: `${(d.value / max) * 100}%`, transition: "width 0.8s ease",
              }} />
            </Box>
          </Box>
        ))}
      </Box>
    </Paper>
  );
};

/* ── Mini Trend Chart (CSS sparkline) ──────────────────────────────── */
const SparklineChart: React.FC<{ data: { date: string; count: number }[]; title: string; color: string }> = ({ data, title, color }) => {
  const max = Math.max(...data.map(d => d.count), 1);
  const last7 = data.slice(-14);
  return (
    <Paper sx={{ p: 3, borderRadius: 3, border: "1px solid #e2e8f0", height: "100%" }}>
      <Typography variant="subtitle1" sx={{ fontWeight: 600, color: T.navy, mb: 2 }}>{title}</Typography>
      <Box sx={{ display: "flex", alignItems: "flex-end", gap: 0.5, height: 120, mt: 1 }}>
        {last7.map((d, i) => (
          <Tooltip key={i} title={`${d.date}: ${d.count}`} arrow>
            <Box sx={{
              flex: 1, borderRadius: "4px 4px 0 0",
              background: alpha(color, 0.7 + (i / last7.length) * 0.3),
              height: `${Math.max((d.count / max) * 100, 4)}%`,
              transition: "height 0.5s ease", cursor: "pointer",
              "&:hover": { background: color, transform: "scaleY(1.05)" },
            }} />
          </Tooltip>
        ))}
      </Box>
      <Box sx={{ display: "flex", justifyContent: "space-between", mt: 1 }}>
        <Typography variant="caption" sx={{ color: T.slate }}>{last7[0]?.date?.slice(5) || ""}</Typography>
        <Typography variant="caption" sx={{ color: T.slate }}>{last7[last7.length - 1]?.date?.slice(5) || ""}</Typography>
      </Box>
    </Paper>
  );
};

/* ── Donut Chart (CSS) ─────────────────────────────────────────────── */
const DonutChart: React.FC<{ data: { label: string; value: number; color: string }[]; title: string }> = ({ data, title }) => {
  const total = data.reduce((s, d) => s + d.value, 0) || 1;
  let cumPercent = 0;
  const gradientParts = data.map(d => {
    const start = cumPercent;
    cumPercent += (d.value / total) * 100;
    return `${d.color} ${start}% ${cumPercent}%`;
  });

  return (
    <Paper sx={{ p: 3, borderRadius: 3, border: "1px solid #e2e8f0", height: "100%" }}>
      <Typography variant="subtitle1" sx={{ fontWeight: 600, color: T.navy, mb: 2 }}>{title}</Typography>
      <Box sx={{ display: "flex", alignItems: "center", gap: 3 }}>
        <Box sx={{
          width: 120, height: 120, borderRadius: "50%", flexShrink: 0,
          background: `conic-gradient(${gradientParts.join(", ")})`,
          position: "relative",
        }}>
          <Box sx={{
            position: "absolute", top: "50%", left: "50%", transform: "translate(-50%, -50%)",
            width: 60, height: 60, borderRadius: "50%", background: T.cardBg,
            display: "flex", alignItems: "center", justifyContent: "center",
          }}>
            <Typography variant="h6" sx={{ fontWeight: 700, color: T.navy }}>{total}</Typography>
          </Box>
        </Box>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
          {data.slice(0, 6).map((d, i) => (
            <Box key={i} sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Box sx={{ width: 10, height: 10, borderRadius: "50%", background: d.color, flexShrink: 0 }} />
              <Typography variant="caption" sx={{ color: T.slate, whiteSpace: "nowrap" }}>
                {d.label}: <strong>{d.value}</strong>
              </Typography>
            </Box>
          ))}
        </Box>
      </Box>
    </Paper>
  );
};

/* ── Main Dashboard Component ──────────────────────────────────────── */
const EmailHubDashboard: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState<DashboardData | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const d = await fetchDashboard();
      setData(d);
    } catch (e) { console.error("Dashboard error:", e); }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const s = data?.stats;
  const c = data?.charts;

  const formatMinutes = (m: number) => {
    if (!m) return "N/A";
    if (m < 60) return `${m}m`;
    return `${Math.floor(m / 60)}h ${m % 60}m`;
  };

  return (
    <Box sx={{ p: 3, background: T.bg, minHeight: "100vh" }}>
      {/* Header */}
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 3 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <IconButton onClick={() => navigate("/email-hub")} size="small">
            <ArrowBackOutlined />
          </IconButton>
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 700, color: T.navy }}>
              Email Hub Dashboard
            </Typography>
            <Typography variant="body2" sx={{ color: T.slate }}>
              Sales conversion & performance overview
            </Typography>
          </Box>
        </Box>
        <Button
          startIcon={<RefreshOutlined />}
          onClick={load}
          variant="outlined"
          size="small"
          sx={{ borderColor: T.gold, color: T.navy, "&:hover": { borderColor: T.navy } }}
        >
          Refresh
        </Button>
      </Box>

      {loading ? (
        <Grid container spacing={2.5}>
          {Array.from({ length: 12 }).map((_, i) => (
            <Grid item xs={12} sm={6} md={3} key={i}>
              <Skeleton variant="rounded" height={120} sx={{ borderRadius: 3 }} />
            </Grid>
          ))}
        </Grid>
      ) : (
        <>
          {/* Stat Cards Row 1 */}
          <Grid container spacing={2.5} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={6} md={3}>
              <StatCard title="New Inquiries Today" value={s?.new_inquiries_today || 0}
                icon={<EmailOutlined />} color="#3b82f6" />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <StatCard title="Unassigned Threads" value={s?.unassigned_threads || 0}
                icon={<PeopleOutlined />} color="#8b5cf6"
                onClick={() => navigate("/email-hub?unassigned=true")} />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <StatCard title="Overdue Threads" value={s?.overdue_threads || 0}
                icon={<WarningAmberOutlined />} color="#ef4444"
                onClick={() => navigate("/email-hub?overdue=true")} />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <StatCard title="Avg First Response" value={formatMinutes(s?.avg_first_response_minutes || 0)}
                icon={<TimerOutlined />} color="#14b8a6" />
            </Grid>
          </Grid>

          {/* Stat Cards Row 2 */}
          <Grid container spacing={2.5} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={6} md={3}>
              <StatCard title="Converted to Quote" value={s?.converted_to_quote || 0}
                icon={<RequestQuoteOutlined />} color="#059669" />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <StatCard title="Converted to Booking" value={s?.converted_to_booking || 0}
                icon={<BookOnlineOutlined />} color="#d97706" />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <StatCard title="Closed Won" value={s?.closed_won || 0}
                icon={<CheckCircleOutlined />} color="#22c55e" />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <StatCard title="Closed Lost" value={s?.closed_lost || 0}
                icon={<CancelOutlined />} color="#dc2626" />
            </Grid>
          </Grid>

          {/* Stat Cards Row 3 */}
          <Grid container spacing={2.5} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={6} md={3}>
              <StatCard title="Follow-up Required" value={s?.follow_up_required || 0}
                icon={<FlagOutlined />} color="#f97316"
                onClick={() => navigate("/email-hub?follow_up=true")} />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <StatCard title="VIP Threads" value={s?.vip_threads || 0}
                icon={<StarOutlined />} color="#eab308"
                onClick={() => navigate("/email-hub?vip=true")} />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <StatCard title="Open Tasks" value={s?.open_tasks || 0}
                icon={<TaskAltOutlined />} color="#6366f1" />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <StatCard title="Overdue Tasks" value={s?.overdue_tasks || 0}
                icon={<AssignmentLateOutlined />} color="#be123c" />
            </Grid>
          </Grid>

          {/* Charts Row */}
          <Grid container spacing={2.5} sx={{ mb: 3 }}>
            <Grid item xs={12} md={8}>
              <SparklineChart
                data={c?.inquiries_by_day || []}
                title="Inquiries by Day (Last 30 Days)"
                color="#3b82f6"
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <DonutChart
                title="Event Type Distribution"
                data={(c?.event_type_distribution || []).map((d, i) => ({
                  label: d.type, value: d.count,
                  color: ["#3b82f6", "#8b5cf6", "#ec4899", "#f59e0b", "#10b981", "#6366f1"][i % 6],
                }))}
              />
            </Grid>
          </Grid>

          <Grid container spacing={2.5} sx={{ mb: 3 }}>
            <Grid item xs={12} md={6}>
              <SimpleBarChart
                title="Conversion by Stage"
                data={(c?.conversion_by_stage || []).map(d => ({
                  label: d.name, value: d.count, color: d.color,
                }))}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <SimpleBarChart
                title="Inquiry Source by Mailbox"
                data={(c?.source_by_mailbox || []).map((d, i) => ({
                  label: d.mailbox, value: d.count,
                  color: ["#3b82f6", "#8b5cf6", "#f59e0b", "#10b981"][i % 4],
                }))}
              />
            </Grid>
          </Grid>

          <Grid container spacing={2.5}>
            <Grid item xs={12}>
              <SparklineChart
                data={(c?.response_time_trend || []).map(d => ({ date: d.date, count: d.avg_minutes }))}
                title="Avg Response Time Trend (minutes)"
                color="#14b8a6"
              />
            </Grid>
          </Grid>
        </>
      )}
    </Box>
  );
};

export default EmailHubDashboard;
